/* /tools/dinner-plate/new — upload menu → AI parse → review → save event */
import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Upload, Sparkles, Loader2, Save, Trash2, ChevronLeft, FileText, X, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { dp } from "../lib/dinnerPlateApi";
import { getConfig } from "../lib/runtimeConfig";
import { toast } from "sonner";

export default function DinnerPlateNewPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload"); // upload | review
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuPaths, setMenuPaths] = useState([]);
  const [title, setTitle] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [orderDeadline, setOrderDeadline] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]); // pre-upload staging
  const [maxFiles, setMaxFiles] = useState(getConfig().dinner_plate_max_menu_files || 3);

  useEffect(() => {
    // Trust the server as authoritative (in case admin just changed the cap).
    dp.runtimeConfig().then(c => c?.max_menu_files && setMaxFiles(c.max_menu_files)).catch(() => {});
  }, []);

  if (!user) return null;

  function onPick(e) {
    const list = Array.from(e.target.files || []);
    if (list.length === 0) return;
    const merged = [...selectedFiles, ...list].slice(0, maxFiles);
    if (selectedFiles.length + list.length > maxFiles) {
      toast.warning(`Only ${maxFiles} file${maxFiles === 1 ? "" : "s"} allowed — extras were skipped`);
    }
    setSelectedFiles(merged);
    // Reset the input so the same file can be re-picked after removal
    if (fileRef.current) fileRef.current.value = "";
  }

  function removeSelected(i) {
    setSelectedFiles(fs => fs.filter((_, idx) => idx !== i));
  }

  async function parseSelected() {
    if (selectedFiles.length === 0) { toast.error("Choose at least one menu file"); return; }
    setParsing(true);
    try {
      const r = selectedFiles.length === 1
        ? await dp.parseMenu(selectedFiles[0])
        : await dp.parseMenuBatch(selectedFiles);
      setCategories(r.categories || []);
      // Normalise storage paths — single vs batch endpoint shapes.
      setMenuPaths(r.menu_storage_paths || (r.menu_storage_path ? [r.menu_storage_path] : []));
      setStep("review");
      const catCount = r.categories?.length || 0;
      const parsedCount = r.parsed_file_count ?? selectedFiles.length;
      toast.success(`Parsed ${parsedCount} file${parsedCount === 1 ? "" : "s"} · ${catCount} categor${catCount === 1 ? "y" : "ies"}`);
      if (r.failed_files?.length) {
        toast.warning(`Couldn't parse: ${r.failed_files.join(", ")}`);
      }
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't parse the menu");
    } finally { setParsing(false); }
  }

  function updateItem(ci, ii, patch) {
    setCategories(cs => cs.map((c, i) => i !== ci ? c : {
      ...c, items: c.items.map((it, j) => j !== ii ? it : { ...it, ...patch })
    }));
  }
  function removeItem(ci, ii) {
    setCategories(cs => cs.map((c, i) => i !== ci ? c : { ...c, items: c.items.filter((_, j) => j !== ii) }));
  }
  function addItem(ci) {
    setCategories(cs => cs.map((c, i) => i !== ci ? c : {
      ...c, items: [...c.items, { id: crypto.randomUUID(), title: "New item", description: "", price: null, dietary_tags: [], options: [] }]
    }));
  }
  function addCategory() {
    setCategories(cs => [...cs, { name: "New section", display_order: cs.length, items: [] }]);
  }

  function moveCategory(ci, delta) {
    setCategories(cs => {
      const to = ci + delta;
      if (to < 0 || to >= cs.length) return cs;
      const next = cs.slice();
      [next[ci], next[to]] = [next[to], next[ci]];
      return next.map((c, i) => ({ ...c, display_order: i }));
    });
  }

  function moveItem(ci, ii, delta) {
    setCategories(cs => cs.map((c, i) => {
      if (i !== ci) return c;
      const to = ii + delta;
      if (to < 0 || to >= c.items.length) return c;
      const items = c.items.slice();
      [items[ii], items[to]] = [items[to], items[ii]];
      return { ...c, items };
    }));
  }

  function removeCategory(ci) {
    if (!window.confirm("Remove this section and all its items?")) return;
    setCategories(cs => cs.filter((_, i) => i !== ci).map((c, i) => ({ ...c, display_order: i })));
  }

  async function save() {
    if (!title.trim()) { toast.error("Give your dinner a title"); return; }
    const empty = categories.every(c => c.items.length === 0);
    if (empty) { toast.error("Add at least one menu item"); return; }
    setSaving(true);
    try {
      const evt = await dp.createEvent({
        title: title.trim(),
        event_date: eventDate ? new Date(eventDate).toISOString() : null,
        restaurant_name: restaurant.trim(),
        categories,
        menu_storage_paths: menuPaths,
        menu_storage_path: menuPaths[0] || null,
        currency,
        order_deadline: orderDeadline ? new Date(orderDeadline).toISOString() : null,
      });
      toast.success("Event created 🍽️");
      nav(`/tools/dinner-plate/events/${evt.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create event");
    } finally { setSaving(false); }
  }

  return (
    <div className="max-w-3xl mx-auto animate-fade-up" data-testid="dp-new-page">
      <Link to="/tools/dinner-plate" className="inline-flex items-center gap-1 text-sm mb-4 hover:underline" style={{ color: "var(--text-muted)" }}>
        <ChevronLeft className="h-4 w-4" /> Back to dinners
      </Link>
      <h1 className="text-3xl font-display font-black tracking-tighter mb-6">New dinner</h1>

      {step === "upload" && (
        <div className="card-tactile rounded-2xl p-8 text-center" data-testid="dp-upload-card">
          <Sparkles className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-ink)" }} />
          <h2 className="font-display tracking-tight text-xl mb-1">Upload the menu</h2>
          <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>
            Photos, PDFs, or plain text — up to <span data-testid="dp-max-files" className="font-semibold">{maxFiles}</span> file{maxFiles === 1 ? "" : "s"}.
            We'll AI-parse each one and stitch the sections together.
          </p>

          <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,text/plain"
                 onChange={onPick} className="hidden" data-testid="dp-file-input" />

          {selectedFiles.length > 0 && (
            <ul className="text-left mb-4 space-y-1" data-testid="dp-staged-files">
              {selectedFiles.map((f, i) => (
                <li key={`${f.name}-${i}`} data-testid={`dp-staged-file-${i}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
                    style={{ background: "var(--surface-2)" }}>
                  <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--primary-ink)" }} />
                  <span className="flex-1 min-w-0 truncate">{f.name}</span>
                  <span className="text-xs shrink-0" style={{ color: "var(--text-muted)" }}>
                    {(f.size / 1024).toFixed(0)} KB
                  </span>
                  <button onClick={() => removeSelected(i)} data-testid={`dp-staged-remove-${i}`}
                          disabled={parsing}
                          className="h-6 w-6 rounded flex items-center justify-center hover:bg-[color:var(--surface)] disabled:opacity-40"
                          title="Remove"><X className="h-3.5 w-3.5" style={{ color: "var(--text-muted)" }} /></button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={parsing || selectedFiles.length >= maxFiles}
              data-testid="dp-add-files-button"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg text-sm font-semibold border disabled:opacity-50"
              style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}
            >
              <Upload className="h-4 w-4" />
              {selectedFiles.length === 0 ? "Choose files" : `Add more (${maxFiles - selectedFiles.length} left)`}
            </button>
            <button
              onClick={parseSelected}
              disabled={parsing || selectedFiles.length === 0}
              data-testid="dp-upload-button"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: "var(--primary)" }}
            >
              {parsing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {parsing ? "Parsing…" : `Parse ${selectedFiles.length || ""} file${selectedFiles.length === 1 ? "" : "s"}`.trim()}
            </button>
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-5" data-testid="dp-review">
          <div className="card-tactile rounded-2xl p-5 space-y-3">
            <h2 className="font-display tracking-tight text-lg">Event details</h2>
            <label className="block">
              <span className="label-caps block mb-1">Title</span>
              <input value={title} onChange={e => setTitle(e.target.value)} data-testid="dp-title"
                     placeholder="Sarah's 30th birthday dinner"
                     className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                     style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="label-caps block mb-1">Restaurant</span>
                <input value={restaurant} onChange={e => setRestaurant(e.target.value)} data-testid="dp-restaurant"
                       className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                       style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
              </label>
              <label className="block">
                <span className="label-caps block mb-1">Date &amp; time</span>
                <input type="datetime-local" value={eventDate} onChange={e => setEventDate(e.target.value)}
                       data-testid="dp-date"
                       className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                       style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
              </label>
              <label className="block">
                <span className="label-caps block mb-1">Currency</span>
                <select value={currency} onChange={e => setCurrency(e.target.value)}
                        data-testid="dp-currency"
                        className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                        style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }}>
                  <option value="GBP">£ Pound (GBP)</option>
                  <option value="EUR">€ Euro (EUR)</option>
                  <option value="USD">$ Dollar (USD)</option>
                </select>
              </label>
              <label className="block">
                <span className="label-caps block mb-1">Order-by deadline (optional)</span>
                <input type="datetime-local" value={orderDeadline} onChange={e => setOrderDeadline(e.target.value)}
                       data-testid="dp-deadline"
                       className="w-full px-3 h-10 rounded-lg border bg-transparent text-sm"
                       style={{ borderColor: "var(--border-subtle)", color: "var(--text-main)" }} />
              </label>
            </div>
          </div>

          {categories.map((cat, ci) => (
            <div key={ci} data-testid={`dp-cat-${ci}`} className="card-tactile rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3 border-b pb-2" style={{ borderColor: "var(--border-subtle)" }}>
                <input value={cat.name} onChange={e => setCategories(cs => cs.map((c, i) => i === ci ? { ...c, name: e.target.value } : c))}
                       className="flex-1 text-lg font-display font-bold tracking-tight bg-transparent"
                       style={{ color: "var(--primary-ink)" }} />
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => moveCategory(ci, -1)} disabled={ci === 0}
                          data-testid={`dp-cat-up-${ci}`}
                          className="h-8 w-8 rounded flex items-center justify-center hover:bg-[color:var(--surface-2)] disabled:opacity-30"
                          style={{ color: "var(--text-muted)" }} title="Move up"><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => moveCategory(ci, +1)} disabled={ci === categories.length - 1}
                          data-testid={`dp-cat-down-${ci}`}
                          className="h-8 w-8 rounded flex items-center justify-center hover:bg-[color:var(--surface-2)] disabled:opacity-30"
                          style={{ color: "var(--text-muted)" }} title="Move down"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => removeCategory(ci)}
                          data-testid={`dp-cat-remove-${ci}`}
                          className="h-8 w-8 rounded flex items-center justify-center hover:bg-[color:var(--surface-2)]"
                          style={{ color: "var(--text-muted)" }} title="Remove section"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="space-y-2">
                {cat.items.map((it, ii) => (
                  <div key={it.id} data-testid={`dp-item-${ci}-${ii}`}
                       className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center p-2 rounded-lg"
                       style={{ background: "var(--surface-2)" }}>
                    <input value={it.title} onChange={e => updateItem(ci, ii, { title: e.target.value })}
                           className="flex-1 px-2 h-9 rounded border-0 bg-transparent text-sm font-medium"
                           style={{ color: "var(--text-main)" }} />
                    <input type="number" step="0.01" value={it.price ?? ""}
                           onChange={e => updateItem(ci, ii, { price: e.target.value ? Number(e.target.value) : null })}
                           placeholder="Price"
                           className="w-24 px-2 h-9 rounded border-0 bg-transparent text-sm"
                           style={{ color: "var(--text-main)" }} />
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => moveItem(ci, ii, -1)} disabled={ii === 0}
                              data-testid={`dp-item-up-${ci}-${ii}`}
                              className="h-9 w-9 rounded flex items-center justify-center hover:bg-[color:var(--surface)] disabled:opacity-30"
                              style={{ color: "var(--text-muted)" }} title="Move up"><ArrowUp className="h-4 w-4" /></button>
                      <button onClick={() => moveItem(ci, ii, +1)} disabled={ii === cat.items.length - 1}
                              data-testid={`dp-item-down-${ci}-${ii}`}
                              className="h-9 w-9 rounded flex items-center justify-center hover:bg-[color:var(--surface)] disabled:opacity-30"
                              style={{ color: "var(--text-muted)" }} title="Move down"><ArrowDown className="h-4 w-4" /></button>
                      <button onClick={() => removeItem(ci, ii)} className="h-9 w-9 rounded flex items-center justify-center hover:bg-[color:var(--surface)]"
                              style={{ color: "var(--text-muted)" }} title="Remove"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
                <button onClick={() => addItem(ci)} className="text-xs hover:underline" style={{ color: "var(--primary-ink)" }}>+ Add item</button>
              </div>
            </div>
          ))}
          <button onClick={addCategory} className="text-sm hover:underline" style={{ color: "var(--primary-ink)" }}>+ Add section</button>

          <div className="sticky bottom-4 flex justify-end gap-2">
            <button onClick={save} disabled={saving}
                    data-testid="dp-save-event"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-xl font-semibold text-sm text-white shadow-lg disabled:opacity-50"
                    style={{ background: "var(--primary)" }}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Create event
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
